// 분기 슬롯 탭 바 기하 — 노드 위 배치 좌표 + "겹칠 때만 밀어낸다" 겹침 해소(순수, DOM 미접촉).
//
// 배치는 **자기 branch 노드 rect 좌상단 바로 위**이고 전체 슬롯을 상시 그린다(2026-08-31 사용자
// 확정 — 원래 방식). 같은 날 두 대안을 시도했다가 되돌렸다: A4 박스 왼쪽 여백 열은 문서를 가리지
// 않는 대신 시선이 문서와 열 사이를 왕복해야 했고, 활성 슬롯만 담은 칩 + hover 확장은 선택지를
// 보려면 마우스를 올려야 했다. 바로 위 형제를 한 줄 가리는 것은 감수한 비용이라, 줄이려면 배치가
// 아니라 lift·높이·톤을 만진다.
//
// 한때(2026-07-03) 중첩 탭 겹침을 없애려고 "최상위 branch rect 한 곳에 활성 경로 체인
// (`[슬롯A|슬롯B] → [슬롯C|슬롯D] → …`) 1개"로 몰아 놓았는데, root branch가 문서 전체를 감싸는
// 템플릿(TBM)에서는 모든 분기 탭이 문서 맨 위로 몰려 어느 탭이 어느 노드 것인지 알 수 없었다.
// 겹침은 자리를 합치는 대신 **겹칠 때만 밀어내기**로 푼다 — 탭과 노드의 대응이 남는다.

/** 밀기 전 기준 위치의 탭 바 1개(캔버스 상대좌표). */
export interface TabBarBox {
  /** 바 식별자(오프셋 결과의 키) — 본문은 branchId, 작성 탭은 `branchId#인스턴스순번`. */
  id: string;
  /** 기준 top — 밀림이 누적되지 않게 **노드 rect에서 매번 다시 계산한 값**이어야 한다. */
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * 펼친 바의 가로 상한(px). 슬롯이 많은 바가 한 줄로 뻗으면 문서를 가로로 길게 덮으므로, 상한을
 * 넘으면 flex-wrap으로 줄을 접고 늘어난 높이는 겹침 밀어내기가 실측으로 흡수한다.
 *
 * ⚠️ **상수여야 한다** — 예전의 `canvas.clientWidth - rect.left` 식 상한처럼 위치에 의존하면 폭이
 * 위치를, 위치가 다시 폭을 결정해 측정이 수렴하지 않는다.
 */
export const TAB_BAR_MAX_WIDTH = 260;

/** 바 아랫변과 노드 rect 윗변 사이 간격(px) — 붙으면 노드 테두리와 한 덩어리로 보인다. */
export const TAB_BAR_LIFT = 2;

/**
 * 탭 한 줄 높이(px) — BranchSlotTabBar의 렌더 높이와 짝. 문서 위에 얹히는 배치라 가림을 줄이려고
 * 낮게 둔다(15 → 13, 2026-08-31).
 */
export const TAB_HEIGHT = 13;

/**
 * 아직 실측 전인 첫 커밋용 top 추정 — 한 줄 바 기준. 실측값이 오면 곧바로 대체되지만, 추정이
 * 없으면 첫 커밋이 노드 top(=바 아랫변 자리)에 그려져 실측 후 한 줄 높이만큼 튄다.
 */
export function estimatedBarTop(nodeTop: number): number {
  return nodeTop - TAB_HEIGHT - TAB_BAR_LIFT;
}

/**
 * 탭 바를 자기 branch 노드 **바로 위**에 놓았을 때의 기준 박스(캔버스 상대좌표).
 *
 * 세로는 바의 **아랫변**을 노드 윗변에 맞춘다(`top = nodeTop - height - lift`). 윗변을 맞춰 아래로
 * 늘리면 바가 노드 본문을 덮고, 고정 lift로 윗변을 잡으면 줄이 접힌 바가 노드 안으로 파고든다.
 * 아랫변 고정이라 슬롯이 늘어 **줄이 접혀도 위로만 자란다** — 노드와의 간격은 늘 같다.
 *
 * 가로는 노드 왼쪽 가장자리에 맞춘다 — "이 노드의 분기"가 왼쪽 정렬로 읽히고, 폭이 달라져도
 * 시작점이 흔들리지 않는다.
 *
 * ⚠️ 되먹임 없음: `width`/`height`는 렌더된 바에서 실측하되 **위치와 무관하게 정해지는 값**이라
 * (가로 상한이 상수) 오프셋을 적용해도 다음 회차 입력이 흔들리지 않는다. height가 top에 들어가지만
 * 그 반대(top이 height를 바꾸는 경로)가 없으므로 계산은 한 번에 수렴한다.
 */
export function nodeTabBarBox(
  id: string,
  nodeTop: number,
  nodeLeft: number,
  width: number,
  height: number,
  lift: number = TAB_BAR_LIFT,
): TabBarBox {
  return { id, top: nodeTop - height - lift, left: nodeLeft, width, height };
}

/** 밀어낸 바와 위 바 사이 최소 세로 간격(px) — 딱 붙으면 두 바가 한 덩어리로 보인다. */
const PUSH_GAP = 2;

/** 두 바의 x 구간이 겹치는가(경계 접촉은 겹침 아님). */
function xOverlaps(a: TabBarBox, b: TabBarBox): boolean {
  return a.left < b.left + b.width && b.left < a.left + a.width;
}

/**
 * 겹치는 바만 아래로 밀어낸 결과를 `{ 바 id: 내린 px }`로 돌려준다. 안 밀린 바는 키 자체가
 * 없다(= 겹침이 없으면 `{}`) — 오프셋이 전면 적용이 아니라 겹침 반응임을 결과 형태가 증언한다.
 *
 * 규칙: 위에 있는 바가 제자리를 지키고 아래가 밀린다. top 오름차순 **안정** 정렬이라 top이 같으면
 * 입력 순서(부모 → 자식 트리 순서)가 남아 부모가 고정된다. 밀어낸 자리에서 또 다른 바와 겹치면
 * 다시 밀어 연쇄를 처리한다.
 *
 * ⚠️ 노드 위 배치에서는 x 판정이 실제로 일을 한다 — 나란한 두 열의 branch처럼 좌우로 비켜 선
 * 바들은 y가 같아도 서로를 밀지 않는다(여백 열 배치였을 땐 모든 바가 x를 공유해 늘 참이었다).
 * 밀리는 쪽은 아래로 내려가 자기 노드를 덮게 되므로, 필요 없는 밀기는 그 자체로 손해다.
 */
export function resolveTabBarOverlaps(
  boxes: TabBarBox[],
  gap: number = PUSH_GAP,
): Record<string, number> {
  const order = boxes
    .map((box, index) => ({ box, index }))
    .sort((p, q) => p.box.top - q.box.top || p.index - q.index);

  /** 이미 자리를 확정한 바들(기하는 box, 확정 top은 별도 — box.top은 기준값 그대로 둔다). */
  const placed: Array<{ box: TabBarBox; top: number }> = [];
  const offsets: Record<string, number> = {};

  for (const { box } of order) {
    let top = box.top;
    // 밀어낸 자리가 또 다른 바와 겹칠 수 있다(연쇄). 회차마다 top이 최소 gap 이상 커지고 후보는
    // 유한하므로 placed.length + 1회 안에 반드시 멈춘다.
    for (let pass = 0; pass <= placed.length; pass += 1) {
      let pushedTo = top;
      for (const p of placed) {
        if (!xOverlaps(box, p.box)) continue;
        // y 구간 미교차(위/아래로 완전히 비켜 있음)면 겹침 아님.
        if (top >= p.top + p.box.height || top + box.height <= p.top) continue;
        pushedTo = Math.max(pushedTo, p.top + p.box.height + gap);
      }
      if (pushedTo === top) break;
      top = pushedTo;
    }
    placed.push({ box, top });
    if (top !== box.top) offsets[box.id] = top - box.top;
  }

  return offsets;
}
